import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/hooks/onboarding/portfolio/errors/errorText";
import type { PortfolioUploadType } from "@/src/components/PortfolioScreenshotModal/types";

const I18n = I18nUtil.createScoped(Translations);

export type GetPortfolioRecognizeErrorTextParams = {
  errorCode?: string;
  uploadType?: PortfolioUploadType;
  /** Reserved for future i18n; unused in placeholder copy. */
  locale?: string;
};

export type GetPortfolioSaveErrorTextParams = {
  /** Reserved for future i18n; unused in placeholder copy. */
  locale?: string;
};

export type GetPortfolioScreenshotRecognizedInstructionParams = {
  /** Reserved; instruction stays fixed English for Agent tool message. */
  locale?: string;
};

const PORTFOLIO_SCREENSHOT_UPLOADED_INSTRUCTION =
  "The user uploaded portfolio files as this tool message's attachments. Inspect the attachments, extract financial records if possible, and decide whether to save them with the available financial tools. Do not assume the portfolio has already been recognized or saved.";

function buildUnrecognizableErrorText(uploadType: PortfolioUploadType): string {
  if (uploadType === "pdf") {
    return I18n.t(
      "No assets detected in the file. Please upload a different file.",
    );
  }
  return I18n.t(
    "No assets detected in the image. Please upload a different image.",
  );
}

/**
 * Inline recognition error copy for the portfolio upload modal.
 */
export function getPortfolioRecognizeErrorText({
  errorCode,
  uploadType = "image",
  locale: _locale,
}: GetPortfolioRecognizeErrorTextParams): string {
  void _locale;
  if (errorCode === "UNRECOGNIZABLE") {
    return buildUnrecognizableErrorText(uploadType);
  }
  return I18n.t(
    "Asset recognition failed. Please select another method to connect your assets.",
  );
}

/**
 * Inline save error copy for the portfolio upload modal.
 */
export function getPortfolioSaveErrorText({
  locale: _locale,
}: GetPortfolioSaveErrorTextParams = {}): string {
  void _locale;
  return I18n.t(
    "Asset save failed. Please select another method to connect your assets.",
  );
}

/** Fixed English instruction sent to Agent via tool message; not shown in UI. */
export function getPortfolioScreenshotUploadedInstruction({
  locale: _locale,
}: GetPortfolioScreenshotRecognizedInstructionParams = {}): string {
  void _locale;
  return PORTFOLIO_SCREENSHOT_UPLOADED_INSTRUCTION;
}

/** @deprecated Use getPortfolioScreenshotUploadedInstruction. */
export function getPortfolioScreenshotRecognizedInstruction(
  params: GetPortfolioScreenshotRecognizedInstructionParams = {},
): string {
  return getPortfolioScreenshotUploadedInstruction(params);
}
